import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Typography, Button, Table, Tag, Select, DatePicker, Input, Space, Empty, Skeleton } from 'antd';
import {
  SyncOutlined, CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  getEstado, getRitmo, getHistorico,
  computeFaseActual, formatDuracion,
  type BrevoSyncEstadoResponse, type BrevoSyncRitmoPunto,
  type BrevoSyncHistoricoItem, type HistoricoFilters,
  fmtFechaHoraMadrid, fmtFechaHoraSegundosMadrid, huellaHistorico,
} from '../api/brevoSyncApi';
import PhaseProgress from '../components/BrevoSync/PhaseProgress';
import { BannerSync, configurarBanner, getVisualState, useLocalElapsed } from '../components/BrevoSync/StatusBanner';
import StatCards from '../components/BrevoSync/StatCards';
import SistemasModal from '../components/BrevoSync/SistemasModal';
import MarcarFallidaModal from '../components/BrevoSync/MarcarFallidaModal';
import LogModal from '../components/BrevoSync/LogModal';
import GcPagina from '../components/Layout/GcPagina';
import { FctEstado, FctTarjeta, fmtNumero, type FctFamilia } from '../fct/react';
import { fct } from '../fct/tokens/tokens';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// ═══════════════════════════════════════════════════════════════
// Banner de estado con acciones de Sistemas
// ═══════════════════════════════════════════════════════════════

interface SistemasBannerProps {
  estado: BrevoSyncEstadoResponse | null;
  ritmo?: BrevoSyncRitmoPunto[];
  loading: boolean;
  onMarcarFallida: (id: number) => void;
  onVerLog: (id: number) => void;
}

/**
 * Misma banda que `StatusBanner` (estado visual, sondeo y textos compartidos; R-15), con las
 * acciones de Sistemas mientras hay una ejecución en marcha.
 */
const SistemasBanner: React.FC<SistemasBannerProps> = ({ estado, ritmo, loading, onMarcarFallida, onVerLog }) => {
  const liveElapsed = useLocalElapsed(estado?.running);

  if (loading && !estado) return <Skeleton active paragraph={{ rows: 1 }} style={{ marginBottom: 16 }} />;

  const vs = getVisualState(estado, ritmo);
  const r = estado?.running;
  const cfg = configurarBanner(vs, estado, liveElapsed);
  const isRunningState = ['initializing', 'slow_load', 'in_progress', 'stuck'].includes(vs);

  const actions = isRunningState && r ? (
    <Space style={{ marginTop: cfg.modo === 'aviso' ? 8 : 0 }}>
      <Button size="small" danger={vs === 'stuck'} disabled={r.cancelacionPedida} onClick={() => onMarcarFallida(r.id)}>
        {r.cancelacionPedida ? 'Parada pedida' : 'Parar'}
      </Button>
      <Button size="small" onClick={() => onVerLog(r.id)}>Ver log</Button>
    </Space>
  ) : undefined;

  return (
    <div style={{ marginBottom: 16 }}>
      <BannerSync cfg={cfg} acciones={actions} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Histórico ampliado para Sistemas
// ═══════════════════════════════════════════════════════════════

interface SistemasHistoryProps {
  onVerLog: (id: number) => void;
  /** Cambia cuando cambia la ejecución viva o la última acabada (huellaHistorico): el listado se pone al día solo (#247). */
  huella?: string;
}

const tipoLabels: Record<string, string> = {
  diario: 'Diario',
  inicial: 'Carga inicial',
  setup_atributos: 'Setup atrib.',
  seed_control: 'Seed control',
};

const SistemasHistory: React.FC<SistemasHistoryProps> = ({ onVerLog, huella }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BrevoSyncHistoricoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [estadoFilter, setEstadoFilter] = useState<string | undefined>(undefined);
  const [tipoFilter, setTipoFilter] = useState<string | undefined>(undefined);
  const [searchId, setSearchId] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(
    [dayjs().subtract(7, 'day'), dayjs()]
  );

  // silencioso: al ponerse al día con el estado, sin el indicador de carga, para que la tabla no parpadee.
  const loadData = async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const filters: HistoricoFilters = {
        page, pageSize,
        tipo: tipoFilter,
        estado: estadoFilter,
        q: searchId || undefined,
        desde: dateRange?.[0]?.format('YYYY-MM-DD'),
        hasta: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const result = await getHistorico(filters);
      setData(result.ejecuciones);
      setTotal(result.total);
    } catch { /* silently fail */ } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [page, pageSize, estadoFilter, tipoFilter, searchId, dateRange]);

  // Al cambiar la huella (lanzar, avanzar, acabar), el listado se vuelve a leer; la primera la cubre la carga inicial (#247).
  const huellaVista = useRef(huella);
  const cargar = useRef(loadData);
  cargar.current = loadData;
  useEffect(() => {
    if (huella === huellaVista.current) return;
    huellaVista.current = huella;
    cargar.current(true);
  }, [huella]);

  // Icono en una tinta (R-14): el estado lo dice la pastilla de al lado, con color, icono y etiqueta.
  const estadoIcon = (estado: string) => {
    switch (estado) {
      case 'ok': return <CheckCircleOutlined />;
      case 'error': return <CloseCircleOutlined />;
      case 'running': return <SyncOutlined spin />;
      default: return null;
    }
  };

  const estadoTag = (estado: string, item: BrevoSyncHistoricoItem) => {
    if (estado === 'ok' && (item.faseAErrores > 0 || item.faseBErrores > 0)) {
      return <FctEstado personalizado={{ etiqueta: `${item.faseAErrores + item.faseBErrores} errores`, familia: 'aviso', icono: 'alerta' }} />;
    }
    const map: Record<string, { familia: FctFamilia; label: string }> = {
      ok: { familia: 'exito', label: 'Correcta' },
      error: { familia: 'riesgo', label: 'Fallida' },
      running: { familia: 'proceso', label: 'En curso' },
      pendiente: { familia: 'proceso', label: 'Pendiente' },
      cancelada: { familia: 'neutro', label: 'Parada' },
    };
    const cfg = map[estado] || { familia: 'neutro', label: estado };
    return <FctEstado personalizado={{ etiqueta: cfg.label, familia: cfg.familia }} />;
  };

  const columns: ColumnsType<BrevoSyncHistoricoItem> = [
    {
      title: 'ID', dataIndex: 'id', key: 'id', width: 60, className: 'fct-mono',
      render: (val: number) => <Text type="secondary" style={{ fontSize: 12 }}>#{val}</Text>,
    },
    {
      title: 'Fecha', dataIndex: 'inicio', key: 'fecha', width: 150, className: 'fct-num', align: 'right',
      render: (val: string) => fmtFechaHoraMadrid(val),
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 110,
      render: (val: string) => <Tag>{tipoLabels[val] || val}</Tag>,
    },
    { title: '', key: 'icon', width: 30, render: (_, r) => estadoIcon(r.estado) },
    {
      title: 'Resultado', key: 'resultado', width: 180,
      render: (_, r) => {
        if (r.estado === 'running' || r.estado === 'pendiente') return estadoTag(r.estado, r);
        const total = r.faseAExitos + r.faseBExitos;
        return <span><span className="fct-mono">{fmtNumero(total)}</span> contactos {estadoTag(r.estado, r)}</span>;
      },
    },
    {
      title: 'Errores', key: 'errores', width: 70, className: 'fct-num', align: 'right',
      render: (_, r) => {
        const errores = r.faseAErrores + r.faseBErrores;
        return errores === 0 ? <Text type="secondary">0</Text> : <Text type="danger">{fmtNumero(errores)}</Text>;
      },
    },
    {
      title: 'Duración', dataIndex: 'duracionSegundos', key: 'duracion', width: 100, className: 'fct-num', align: 'right',
      render: (val: number) => formatDuracion(val),
    },
    {
      title: 'Parámetros', dataIndex: 'parametros', key: 'parametros', width: 140, ellipsis: true,
      render: (val: string | undefined) => val ? <Text code style={{ fontSize: 11 }}>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Origen', dataIndex: 'origen', key: 'origen', width: 90,
      render: (val: string) => <Text type="secondary" style={{ textTransform: 'capitalize' }}>{val}</Text>,
    },
    {
      title: 'Lanzada por', dataIndex: 'usuarioAlta', key: 'usuarioAlta', width: 160, ellipsis: true,
      render: (val: string | undefined) => val ? <Text style={{ fontSize: 12 }}>{val}</Text> : <Text type="secondary">—</Text>,
    },
  ];

  const expandedRowRender = (record: BrevoSyncHistoricoItem) => (
    <div style={{ padding: '8px 0' }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text><strong>Inicio:</strong> <span className="fct-mono">{fmtFechaHoraSegundosMadrid(record.inicio)}</span></Text>
        {record.fin && <Text><strong>Fin:</strong> <span className="fct-mono">{fmtFechaHoraSegundosMadrid(record.fin)}</span></Text>}
        <Text><strong>Fase A:</strong> <span className="fct-mono">{fmtNumero(record.faseAExitos)}</span> OK, <span className="fct-mono">{fmtNumero(record.faseAErrores)}</span> errores{record.faseATotal ? <> de <span className="fct-mono">{fmtNumero(record.faseATotal)}</span></> : ''}</Text>
        <Text><strong>Fase B:</strong> <span className="fct-mono">{fmtNumero(record.faseBExitos)}</span> OK, <span className="fct-mono">{fmtNumero(record.faseBErrores)}</span> errores{record.faseBTotal ? <> de <span className="fct-mono">{fmtNumero(record.faseBTotal)}</span></> : ''}</Text>
        <Text><strong>Duplicados fusionados:</strong> <span className="fct-mono">{fmtNumero(record.duplicadosFusionados)}</span></Text>
        {record.parametros && <Text><strong>Parámetros:</strong> {record.parametros}</Text>}
        {record.errorDetalle && (
          <div style={{ background: fct.estado.riesgo.relleno, padding: '8px 12px', borderRadius: 4, marginTop: 4 }}>
            <Text type="danger" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{record.errorDetalle}</Text>
          </div>
        )}
        <Button size="small" icon={<FileTextOutlined />} onClick={() => onVerLog(record.id)} style={{ marginTop: 8 }}>Ver log</Button>
      </Space>
    </div>
  );

  return (
    <FctTarjeta titulo="Histórico de sincronizaciones" style={{ marginTop: 16 }}>
      <div className="fct-filtros" style={{ marginBottom: 12 }}>
        <RangePicker value={dateRange} onChange={(dates) => { setDateRange(dates); setPage(1); }} format="DD/MM/YYYY" placeholder={['Desde', 'Hasta']} size="small" />
        <Select value={estadoFilter} onChange={(val) => { setEstadoFilter(val); setPage(1); }} placeholder="Estado" allowClear size="small" style={{ width: 140 }}
          options={[{ label: 'Correctas', value: 'ok' }, { label: 'Fallidas', value: 'error' }, { label: 'En curso', value: 'running' }]} />
        <Select value={tipoFilter} onChange={(val) => { setTipoFilter(val); setPage(1); }} placeholder="Tipo" allowClear size="small" style={{ width: 150 }}
          options={[{ label: 'Diario', value: 'diario' }, { label: 'Carga inicial', value: 'inicial' }, { label: 'Setup atributos', value: 'setup_atributos' }, { label: 'Seed control', value: 'seed_control' }]} />
        <Input value={searchId} onChange={e => { setSearchId(e.target.value); setPage(1); }} placeholder="Buscar por ID" allowClear size="small" style={{ width: 120 }} />
      </div>
      <Table
        dataSource={data.map(d => ({ ...d, key: d.id }))} columns={columns} loading={loading}
        expandable={{ expandedRowRender }}
        pagination={{
          current: page, pageSize, total,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showSizeChanger: true, pageSizeOptions: ['10', '30', '50', '100'],
          showTotal: (t) => `${t} ejecuciones`, size: 'small',
        }}
        size="small" locale={{ emptyText: <Empty description="Sin ejecuciones registradas" /> }}
      />
    </FctTarjeta>
  );
};

// ═══════════════════════════════════════════════════════════════
// Página principal de Sistemas
// ═══════════════════════════════════════════════════════════════

const BrevoSyncSistemasPage: React.FC = () => {
  const [estado, setEstado] = useState<BrevoSyncEstadoResponse | null>(null);
  const [ritmo, setRitmo] = useState<BrevoSyncRitmoPunto[]>([]);
  const [loading, setLoading] = useState(true);
  const [sistemasVisible, setSistemasVisible] = useState(false);
  const [marcarFallidaVisible, setMarcarFallidaVisible] = useState(false);
  const [marcarFallidaId, setMarcarFallidaId] = useState<number | null>(null);
  const [logVisible, setLogVisible] = useState(false);
  const [logId, setLogId] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const isRunning = !!estado?.running;
  const isLoadingPhase = isRunning && estado?.running ? computeFaseActual(estado.running) === 'loading' : false;

  const fetchEstado = useCallback(async () => {
    try {
      const data = await getEstado();
      setEstado(data);
      setLoading(false);
      if (data.running) {
        try {
          const ritmoData = await getRitmo(data.running.id, 'A');
          setRitmo(ritmoData.puntos);
        } catch { /* silently fail */ }
      }
    } catch { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchEstado();
    const setupInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const delay = isRunning ? (isLoadingPhase ? 20000 : 5000) : 60000;
      intervalRef.current = setInterval(fetchEstado, delay);
    };
    setupInterval();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isRunning, isLoadingPhase, fetchEstado]);

  useEffect(() => {
    const handleVisibility = () => { if (document.visibilityState === 'visible') fetchEstado(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchEstado]);

  const handleLaunched = () => {
    let attempts = 0;
    const poll = () => { fetchEstado(); attempts++; if (attempts < 15) setTimeout(poll, 2000); };
    setTimeout(poll, 1000);
  };

  const openLog = (id: number) => { setLogId(id); setLogVisible(true); };
  const openMarcarFallida = (id: number) => { setMarcarFallidaId(id); setMarcarFallidaVisible(true); };

  return (
    <GcPagina
      seccion="Brevo"
      titulo="Sincronización Brevo"
      contexto="Vista de Sistemas"
      densidad="compacta"
      volver={{ to: '/brevo-sync', etiqueta: 'Volver a Sincronización Brevo' }}
      acciones={
        <Button type="primary" icon={<SyncOutlined />} onClick={() => setSistemasVisible(true)}>
          Lanzar sincronización
        </Button>
      }
    >
      {/* Banner con acciones */}
      <SistemasBanner
        estado={estado} ritmo={ritmo} loading={loading}
        onMarcarFallida={openMarcarFallida} onVerLog={openLog}
      />

      <FctTarjeta titulo="Estado de la sincronización">
        {/* Progreso por fases */}
        {estado?.running && <PhaseProgress ejecucion={estado.running} />}

        {/* Cards de métricas */}
        <StatCards ejecucion={estado?.running} ultimaFinalizada={estado?.ultimaFinalizada} ritmo={ritmo} />
      </FctTarjeta>

      {/* Histórico ampliado */}
      <SistemasHistory onVerLog={openLog} huella={huellaHistorico(estado)} />

      {/* Modales */}
      <SistemasModal visible={sistemasVisible} hasRunning={isRunning} onClose={() => setSistemasVisible(false)} onLaunched={handleLaunched} />
      <MarcarFallidaModal visible={marcarFallidaVisible} executionId={marcarFallidaId} onClose={() => setMarcarFallidaVisible(false)} onMarked={() => fetchEstado()} />
      <LogModal visible={logVisible} executionId={logId} onClose={() => setLogVisible(false)} />
    </GcPagina>
  );
};

export default BrevoSyncSistemasPage;
